import * as React from 'react';

import { cn } from '../../services/lib/utils';
import { SearchIcon } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  as?: 'input' | 'textarea';
  theme?: 'new' | 'default' | 'search';
  showSearchIcon?: boolean;
  label?: string;
}

const searchThemeClasses =
  'w-full rounded-[8px] border border-gray-300 py-3 pl-4 pr-4 font-haffersq text-sh-14-20 text-people-gray-450 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  ({ className, type, theme = 'default', as = 'input', showSearchIcon = false, label, ...props }, ref) => {
    const borders =
      theme === 'search'
        ? searchThemeClasses
        : theme === 'default'
          ? 'font-haffersq text-sh-14-20 text-people-gray-450 border-gray-300 shadow-sm'
          : 'font-haffersq text-sh-14-20 text-people-gray-450 border-gray-300 bg-white ring-offset-background placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

    const inputPadding = showSearchIcon && theme !== 'search' ? 'pr-10' : '';

    const inputElement =
      as === 'input' ? (
        <input
          {...props}
          type={theme === 'search' ? 'search' : type}
          className={cn(
            theme === 'search' ? '' : 'mt-1 block h-[52px] w-full rounded-lg border px-3 py-2',
            className,
            borders,
            inputPadding,
          )}
          ref={ref as React.RefObject<HTMLInputElement>}
          aria-label={theme === 'search' ? (props['aria-label'] ?? 'Search') : props['aria-label']}
        />
      ) : (
        <textarea
          className={cn('mt-1 block w-full rounded-lg border px-3 py-2 ', className, borders, inputPadding)}
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          {...props}
        />
      );

    if (showSearchIcon && theme !== 'search') {
      return (
        <div>
          <p className="mb-2 font-haffer text-sh-14-20 font-bold">{label}</p>

          <div className="relative">
            {inputElement}
            <SearchIcon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-black" />
          </div>
        </div>
      );
    }

    return inputElement;
  },
);
Input.displayName = 'Input';

export { Input };
